import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantAppSessionsChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { bucketStartIso, type ChartGranularity } from '@/lib/chart-axis';
import { groupCampaignsByBucket, markerTime } from '@/lib/campaign-markers';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import { MetricsAreaChart } from '@/components/ui/charts/area-chart';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';
import { ChartCampaignMarkers } from '@/components/ui/charts/chart-campaign-markers';

type SessionsCampaign =
  TenantAppSessionsChartResponseDto['campaigns'][string][number];

interface SessionsChartProps {
  granularity: ChartGranularity;
  data: TenantAppSessionsChartResponseDto;
  /** The filter's period: marks the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
}

/**
 * "Sessões no app" (ANLT-06): sessions per bucket with the partial bucket
 * dashed, and a dot on each bucket with campaigns — the campaigns the
 * endpoint returns per bucket, regrouped by the chart's own buckets.
 */
export function SessionsChart({
  granularity,
  data,
  range,
  syncId,
  syncMethod,
}: SessionsChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(
    () =>
      bucketSumChartData(
        data.points,
        granularity,
        locale,
        (p) => p.sessionsCount,
        'sessions'
      ).map((row) => ({
        month: row.month,
        date: String(row.date),
        sessions: Number(row.sessions),
      })),
    [data.points, granularity, locale]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () => markPartialRows(rows, 'sessions', partial.edges),
    [rows, partial.edges]
  );
  const groups = useMemo(() => {
    const campaigns = Object.values(data.campaigns).flat();
    return range
      ? groupCampaignsByBucket(campaigns, granularity, locale, range)
      : new Map<string, SessionsCampaign[]>();
  }, [data.campaigns, granularity, locale, range]);
  const campaignsOfRow = (row: Record<string, unknown>) => {
    const key =
      typeof row.date === 'string'
        ? bucketStartIso(row.date, granularity, locale)
        : null;
    return (key ? (groups.get(key) ?? []) : []).map((campaign) => ({
      name: campaign.name,
      time: markerTime(campaign.launchDateTime, granularity, locale),
    }));
  };

  const tooltipContent = useChartTooltip({
    campaigns: campaignsOfRow,
    formatRows: (_label, payload) => [
      {
        label: LL.metricsPage.usersTab.sessionsSeries(),
        value: new Intl.NumberFormat(locale).format(
          (payload[0]?.value as number) ?? 0
        ),
      },
    ],
  });

  return (
    <MetricsAreaChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      tooltipContent={tooltipContent}
    >
      <MetricsChartAreaSeries
        dataKey="sessions"
        name={LL.metricsPage.usersTab.sessionsSeries()}
        partialDataKey="sessionsPartial"
        partialLabels={partial.labels}
      />
      <ChartCampaignMarkers
        data={chartData}
        groups={groups}
        granularity={granularity}
        valueKey="sessions"
      />
    </MetricsAreaChart>
  );
}
